import { FeatureFlag } from '@ps/web-core-enums';
import isBefore from 'date-fns/isBefore';

import type { BetslipClearedProps, SelectionRemovedProps } from '@swf/core/analytics';
import { RequestError } from '@swf/core/errors/RequestError';
import type { EffectParams } from '@swf/core/store';
import { select } from '@swf/core/store';
import { BET_SLIP_PERSIST_VERSION } from '@swf/core/store/middleware/persistBetSlipStateMiddleware';
import { canUseDOM, canUseLocalStorage } from '@swf/core/utils';
import type { OpportunitiesResponse, RunnerId } from '@swf/generated/types/api/sports';
import buildSportsRequest from '@swf/sports/utils/buildSportsRequest';

import type { PersistedBetSlipState } from './types/betSlip';

interface RemoveEffectProps {
  runnerId: RunnerId;
  selectionRemovedTrackingMethod: (props: SelectionRemovedProps) => void;
}

type BetslipClearedTrackingMethod = (props: BetslipClearedProps) => void;

type PersistedStateWithExpiryAndVersion = PersistedBetSlipState & {
  exp: string;
  version: string;
};

let requestCancellationController: AbortController;

const effects = {
  async opportunitiesRequested(
    _payload: undefined,
    { dispatch, request, rootState, select }: EffectParams
  ) {
    const selections = select.betSlip.selections(rootState);
    const isPlayMoney = select.currency.isPlayMoney(rootState);

    if (isPlayMoney) return;

    if (!selections.length) {
      requestCancellationController?.abort();
      return;
    }

    requestCancellationController?.abort();

    requestCancellationController = new AbortController();
    const requestCancellationSignal = requestCancellationController.signal;
    const sportsRequest = buildSportsRequest(request, rootState);

    sportsRequest<OpportunitiesResponse>({
      path: '/opportunities',
      method: 'POST',
      body: selections,
      includeFeatures: [FeatureFlag.ISPBetBuilder],
      signal: requestCancellationSignal
    })
      .then(({ body: latestOpportunities }) => {
        dispatch.betSlip.betSlipUpdated(latestOpportunities);
      })
      .catch((err) => {
        if (err instanceof RequestError && err.body.code !== 'ABORT_ERROR') {
          dispatch.betSlip.betSlipUpdateFailed();
        }
      });
  },

  async removeSelectionWithTracking(
    { runnerId, selectionRemovedTrackingMethod }: RemoveEffectProps,
    { dispatch, rootState }: EffectParams
  ) {
    const singleOpportunities = select.betSlip.singleOpportunities(rootState);
    selectionRemovedTrackingMethod({
      singleOpportunities,
      runnerId
    });
    dispatch.betSlip.selectionRemoved(runnerId);
  },

  async clearBetslipWithTracking(
    betslipClearedTrackingMethod: BetslipClearedTrackingMethod,
    { dispatch, rootState }: EffectParams
  ) {
    const singleOpportunities = select.betSlip.singleOpportunities(rootState);
    betslipClearedTrackingMethod(singleOpportunities);
    dispatch.betSlip.selectionsCleared();
  },

  rehydrateBetslip(_payload: unknown, { dispatch }: EffectParams) {
    if (canUseDOM && canUseLocalStorage) {
      try {
        const persistedState: PersistedStateWithExpiryAndVersion | object = JSON.parse(
          localStorage.getItem('betSlip') ?? '{}'
        );

        if (
          'exp' in persistedState &&
          isBefore(new Date(), new Date(persistedState.exp)) &&
          persistedState.version === BET_SLIP_PERSIST_VERSION
        ) {
          const { exp, version, ...state } = persistedState;
          dispatch.betSlip.betSlipRehydrated(state);
        } else {
          dispatch.betSlip.betSlipRehydrated();
          localStorage.removeItem('betSlip');
        }
      } catch (err) {
        dispatch.betSlip.betSlipRehydrated();
        localStorage.removeItem('betSlip');
      }
    }
  },

  betPlacementFailed(_payload: unknown, { dispatch }: EffectParams) {
    dispatch.betSlip.opportunitiesRequested();
  }
};

export default effects;
